import { BlogNewsListDataContainer } from '../blog-news/blog-news';
import { EventListCardData, EventListDataContainer } from '../event/event-types';
import { EditorialCard } from '../editorial-card/editorial-card';
import { isOnDemandEvent } from '../events/helpers/is-on-demand-event';
import { ContentHubProps, CustomPostLink } from './content-hub-types';
import { CustomPostsShape, EntriesShape, PostType } from './query';
import {
    Filters,
    ListCardData,
    findNextUniquePost,
    sortOnDemandEventsFirst,
} from './filters';

type Localize = (key: string, fallback: string) => string;

type Deps = {
    props: ContentHubProps;
    localize: Localize;
    entries: () => EntriesShape | undefined;
    customPostLinkEntries: (() => CustomPostsShape | undefined) | undefined;
    filters: Filters;
};

/** Bundles the per-component card lookup + render helpers. They all close over
 *  `props`, the GraphQL `entries` accessor, the custom-post-link accessor, and
 *  the audience/events filters — so the main component just calls
 *  `cardHelpers.parseCardsData(...)` etc. without re-threading those deps. */
export const createCardHelpers = ({
    props,
    localize,
    entries,
    customPostLinkEntries,
    filters,
}: Deps) => {
    /** Per-card label resolution. Blog cards fall back to the first category
     *  or "Blog"; event cards prioritise status flags (Fully Booked / On
     *  demand) over taxonomy. */
    const resolveCategoryLabel = (post: ListCardData, type: PostType) => {
        if (type === 'event') {
            const event = post as EventListCardData;
            if (event.isFullyBooked) {
                return localize('event-fully-booked', 'Fully Booked');
            }
            if (isOnDemandEvent(event)) {
                return localize('on-demand', 'On demand');
            }
            return event.categories?.[0]?.name ?? localize('event', 'Event');
        }
        return post.categories?.[0]?.name ?? localize('blog', 'Blog');
    };

    const renderCard = (post: ListCardData, type: PostType) => (
        <EditorialCard
            data={post}
            categoryLabel={resolveCategoryLabel(post, type)}
            myContentType={type}
        />
    );

    const renderCustomLinkCard = (link: CustomPostLink, type: PostType) => {
        const card = customPostLinkEntries?.()?.posts.find(
            (entry) => entry.content?.permalink === link.url,
        );
        if (!card?.content) {
            return null;
        }
        return renderCard(card.content, type);
    };

    const parseCardsData = (
        cardIndex: number,
        rendered: string[],
        type: PostType,
        tag = 'NO_TAG',
    ) => {
        const e = entries();
        const data: (BlogNewsListDataContainer | EventListDataContainer)[] =
            type === 'blog-and-news'
                ? [...(e?.blogPosts ?? []), ...(e?.blogPostsWithTags ?? [])]
                : [...(e?.events ?? []), ...(e?.eventsWithTags ?? [])];

        let filtered: ListCardData[] = data
            .map((entry) =>
                entry?.meta
                    ? ({ ...entry.meta, ...entry.meta.extra, key: entry.key, permalink: entry.key } as unknown as ListCardData)
                    : null,
            )
            .filter((p): p is ListCardData => Boolean(p) && filters.audienceFilter(p!))
            .filter((p) => filters.eventsFilter(p as EventListCardData, cardIndex, type));

        if (type === 'event') {
            filtered = sortOnDemandEventsFirst(filtered);
        }
        return findNextUniquePost(tag, filtered, rendered);
    };

    const resolveTypeAndTag = (i: number) => {
        if (props.mixedContent) {
            const card = props.cards[i];
            return { type: card.posttype.value, tag: card?.tag?.slug || 'NO_TAG' };
        }
        return {
            type: props.contentType.posttype.value,
            tag: props.contentType?.tag?.slug || 'NO_TAG',
        };
    };

    return { renderCard, renderCustomLinkCard, parseCardsData, resolveTypeAndTag };
};
